import type { IPeerConnection } from '../../types';
import type { TrackSwapDependencies, TrackSwapRequest, TrackSwapResult, TrackSwapTarget } from './trackSwapContracts';

type TargetState = { target: TrackSwapTarget; pending: boolean; changed: boolean };
const RETRY_DELAYS_MS = [100, 250, 500];

/** Bounded sender installation; capture stays owned by the caller until commit/rollback ends. */
export class VideoTrackSwapTransaction {
  private active?: { request: TrackSwapRequest; result: Promise<TrackSwapResult> };
  private disposed = false;
  constructor(private readonly deps: TrackSwapDependencies) {}

  apply(request: TrackSwapRequest): Promise<TrackSwapResult> {
    if (this.active) {
      if (this.active.request.previousTrack === request.previousTrack && this.active.request.nextTrack === request.nextTrack) return this.active.result;
      return Promise.resolve({ state: 'failed', requestId: request.requestId, reason: 'track-swap-busy' });
    }
    const result = this.run(request);
    const active = { request, result }; this.active = active;
    const clear = () => { if (this.active === active) this.active = undefined; };
    void result.then(clear, clear);
    return result;
  }

  dispose(): void { this.disposed = true; }

  private async run(request: TrackSwapRequest): Promise<TrackSwapResult> {
    const targets = new Map<IPeerConnection, TargetState>();
    let reason = 'sender-track-replacement-failed';
    try {
      for (let attempt = 0; attempt <= RETRY_DELAYS_MS.length; attempt++) {
        this.assertCurrent(request);
        this.collectTargets(targets);
        const pending = [...targets.values()].filter(state => this.deps.isCurrentTarget(state.target) && !this.holds(state.target, request.nextTrack));
        const results = await Promise.allSettled(pending.map(state => this.install(state, request.nextTrack, request.deadlineMs, request)));
        const rejected = results.find((result): result is PromiseRejectedResult => result.status === 'rejected');
        if (rejected) reason = String(rejected.reason instanceof Error ? rejected.reason.message : rejected.reason);
        this.assertCurrent(request);
        this.collectTargets(targets);
        if ([...targets.values()].every(state => !this.deps.isCurrentTarget(state.target) || this.holds(state.target, request.nextTrack))) {
          request.commit();
          for (const state of targets.values()) {
            if (state.changed && this.deps.isCurrentTarget(state.target)) {
              // Capture is committed; a notification failure must not roll senders back
              // underneath the now-published capture.
              try { this.deps.committed?.(state.target); }
              catch (error) { console.error('Track replacement post-commit notification failed', error); }
            }
          }
          return { state: 'applied', requestId: request.requestId };
        }
        if (attempt < RETRY_DELAYS_MS.length) await this.pause(RETRY_DELAYS_MS[attempt], request);
      }
    } catch (error) { reason = error instanceof Error ? error.message : String(error); }
    await this.rollback(targets, request);
    return { state: !request.isCurrent() || this.disposed ? 'superseded' : 'failed', requestId: request.requestId, reason };
  }

  private collectTargets(targets: Map<IPeerConnection, TargetState>): void {
    for (const target of this.deps.targets()) {
      if (!targets.has(target.connection)) targets.set(target.connection, { target, pending: false, changed: false });
    }
  }

  private async install(state: TargetState, to: MediaStreamTrack, deadline: number, request?: TrackSwapRequest): Promise<void> {
    const target = state.target;
    if (!this.deps.isCurrentTarget(target)) return;
    const from = this.videoTracks(target).find(track => track !== to);
    if (!from) {
      if (!this.holds(target, to)) throw new Error('sender-video-track-unavailable');
      return;
    }
    state.pending = true;
    const operation = Promise.resolve().then(() => {
      if (!this.deps.isCurrentTarget(target)) return;
      return this.deps.replace(target, from, to);
    }).then(() => { state.pending = false; }, error => { state.pending = false; throw error; });
    await this.bounded(operation, deadline, request);
    if (this.deps.isCurrentTarget(target) && !this.holds(target, to)) throw new Error('sender-track-not-replaced');
    state.changed = true;
  }

  private async rollback(targets: Map<IPeerConnection, TargetState>, request: TrackSwapRequest): Promise<void> {
    const deadline = Math.min(request.deadlineMs + 1000, this.deps.nowMs() + 1000);
    await Promise.all([...targets.values()].map(async state => {
      if (!this.deps.isCurrentTarget(state.target)) return;
      if (state.pending || request.previousTrack.readyState === 'ended') {
        this.deps.recoverFailedTarget(state.target, 'track-swap-unsettled'); return;
      }
      if (this.holds(state.target, request.previousTrack)) return;
      try { await this.install(state, request.previousTrack, deadline); }
      catch { if (this.deps.isCurrentTarget(state.target)) this.deps.recoverFailedTarget(state.target, 'track-swap-rollback-failed'); }
    }));
  }

  private bounded<T>(operation: Promise<T>, deadline: number, request?: TrackSwapRequest): Promise<T> {
    return new Promise((resolve, reject) => {
      let done = false;
      let cancel: () => void = () => undefined;
      const check = () => {
        if (done) return;
        if (this.deps.nowMs() >= deadline || (request && (this.disposed || !request.isCurrent()))) {
          done = true; reject(new Error('track-swap-deadline-or-superseded')); return;
        }
        cancel = this.deps.schedule(check, Math.min(50, deadline - this.deps.nowMs()));
      };
      check();
      void operation.then(value => { if (!done) { done = true; cancel(); resolve(value); } },
        error => { if (!done) { done = true; cancel(); reject(error); } });
    });
  }

  private pause(delayMs: number, request: TrackSwapRequest): Promise<void> {
    let cancel: () => void = () => undefined;
    const waiting = new Promise<void>(resolve => { cancel = this.deps.schedule(resolve, delayMs); });
    return this.bounded(waiting, request.deadlineMs, request).finally(() => cancel());
  }
  private videoTracks(target: TrackSwapTarget): MediaStreamTrack[] {
    return target.connection.getRtcPeerConnection()?.getSenders().map(sender => sender.track)
      .filter((track): track is MediaStreamTrack => track?.kind === 'video') ?? [];
  }
  private holds(target: TrackSwapTarget, track: MediaStreamTrack): boolean {
    const tracks = this.videoTracks(target);
    return tracks.length > 0 && tracks.every(current => current === track);
  }
  private assertCurrent(request: TrackSwapRequest): void {
    if (this.disposed || !request.isCurrent()) throw new Error('track-swap-superseded');
    if (this.deps.nowMs() >= request.deadlineMs) throw new Error('track-swap-deadline');
    if (request.nextTrack.readyState === 'ended') throw new Error('replacement-track-ended');
  }
}
